import cx from "classnames";
import type { ReactElement } from "react";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "tertiary" | "ghost";
  size?: "small" | "medium" | "large";
  disabled?: boolean;
  icon?: ReactElement;
  iconPosition?: "left" | "right";
}

export default function Button({
  variant = "primary",
  size = "medium",
  disabled,
  icon,
  iconPosition,
  className,
  children,
  ...props
}: ButtonProps): ReactElement {
  // Check if `children` is truly empty (undefined, null, or an empty string)
  const isIconOnly = !children || children === "";

  return (
    <button
      disabled={disabled}
      className={cx(
        "flex w-auto cursor-pointer flex-nowrap items-center rounded-full transition-all duration-200",
        {
          // Variant Styles
          "bg-button-primary text-button-primary-label hover:bg-button-primary-hover":
            variant === "primary",
          "bg-button-secondary text-button-secondary-label hover:bg-button-secondary-hover":
            variant === "secondary",
          "bg-button-tertiary text-button-tertiary-label hover:bg-button-tertiary-hover":
            variant === "tertiary",
          "text-button-tertiary-label hover:bg-button-ghost-hover bg-transparent":
            variant === "ghost",

          // Size Styles
          "text-button-2 h-6 gap-1 p-2": size === "small" && !isIconOnly,
          "text-button-1 gap-2 px-3 py-2": size === "medium" && !isIconOnly,
          "text-button-1 gap-2 px-4 py-2": size === "large" && !isIconOnly,

          // Animate Hover Effect when with icon on the right
          "hover:gap-1.5 hover:pr-1.5":
            size === "small" && iconPosition == "right",
          "hover:gap-3 hover:pr-2":
            size === "medium" && iconPosition == "right",
          "hover:gap-3 hover:pr-3": size === "large" && iconPosition == "right",

          // Icon-Only Button Padding
          "p-1.5":
            (isIconOnly && size === "small") ||
            (isIconOnly && size === "medium"),
          "p-2": isIconOnly && size === "large",

          // Disabled Styles
          "bg-opacity-40 pointer-events-none cursor-none opacity-40": disabled,

          // Icon Position Styles
          "flex-row": icon && iconPosition === "left",
          "flex-row-reverse": icon && iconPosition === "right",
        },
        className,
      )}
      {...props}
    >
      {icon && (
        <span
          className={cx("flex items-center justify-center", {
            "h-3 w-3": size === "small",
            "h-5 w-5": size === "medium",
            "h-6 w-6": size === "large",
          })}
        >
          {icon}
        </span>
      )}
      {children}
    </button>
  );
}
